import { useCallback, useEffect, useState } from 'react';

import { notNil } from '@/lib/utils';
import { useAuth } from '@/auth/auth-context';
import { Account } from '@/workspace/types';
import { Button } from '@/components/button';
import { Icon } from '@/components/icon';

import { Cards, Card } from '../../components/gradient-layout/cards';
import {
  OAuthConsent,
  OAuthConsentResult,
  buildOAuthCallbackUrl,
  describeScope,
  fetchOAuthConsent,
} from './oauth-config';
import { OAuthError, OAuthLoading } from './oauth-status';

import commonStyles from '../public.module.scss';
import styles from './oauth-consent.module.scss';

interface OAuthConsentStepProps {
  oauthSession: string;
  account: Account;
}

// Explicit Allow/Deny gate shown after login + account selection. The Firebase
// ID token is only handed to the callback once the user clicks Allow — denying
// sends no token, so the API cannot issue access.
export const OAuthConsentStep = ({ oauthSession, account }: OAuthConsentStepProps) => {
  const { getToken } = useAuth();
  const [result, setResult] = useState<OAuthConsentResult | null>(null);
  const [pending, setPending] = useState<'allow' | 'deny' | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    fetchOAuthConsent(oauthSession).then((consentResult) => {
      if (!cancelled) {
        setResult(consentResult);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [oauthSession]);

  const allow = useCallback(async () => {
    if (pending !== null) {
      return;
    }
    setPending('allow');
    try {
      const idToken = await getToken();
      window.location.assign(
        buildOAuthCallbackUrl({
          decision: 'allow',
          oauthSession,
          idToken,
          accountId: account.accountId,
        }),
      );
    } catch {
      setPending(null);
      setError('Something went wrong while connecting. Please try again.');
    }
  }, [pending, getToken, oauthSession, account.accountId]);

  const deny = useCallback(() => {
    if (pending !== null) {
      return;
    }
    setPending('deny');
    window.location.assign(buildOAuthCallbackUrl({ decision: 'deny', oauthSession }));
  }, [pending, oauthSession]);

  if (notNil(error)) {
    return <OAuthError>{error}</OAuthError>;
  }

  if (result === null) {
    return <OAuthLoading />;
  }

  if (result.status === 'expired') {
    return (
      <OAuthError>
        This authorization request has expired — start again from your MCP client.
      </OAuthError>
    );
  }

  if (result.status === 'error') {
    return (
      <OAuthError>
        Couldn&apos;t load the authorization request. Please try again from your MCP client.
      </OAuthError>
    );
  }

  return (
    <OAuthConsentScreen
      consent={result.consent}
      account={account}
      pending={pending}
      onAllow={allow}
      onDeny={deny}
    />
  );
};

interface OAuthConsentScreenProps {
  consent: OAuthConsent;
  account: Account;
  pending: 'allow' | 'deny' | null;
  onAllow: () => void;
  onDeny: () => void;
}

const OAuthConsentScreen = ({
  consent,
  account,
  pending,
  onAllow,
  onDeny,
}: OAuthConsentScreenProps) => (
  <div className={commonStyles.layout}>
    <h1>Authorize access</h1>
    <Cards>
      <Card className={styles.consentCard}>
        <div className={styles.client}>
          <ClientLogo name={consent.clientName} logoUri={consent.logoUri} />
          <div className={styles.clientName}>{consent.clientName}</div>
          {consent.verified ? (
            <div className={styles.verified}>
              <Icon name="CheckCircle" size={14} />
              Verified by Supersimple
            </div>
          ) : (
            <div className={styles.notVerified}>Not verified by Supersimple</div>
          )}
        </div>

        <p className={styles.grantTarget}>
          This will grant access to <strong>{consent.redirectHost}</strong>
        </p>

        <div className={styles.account}>
          Authorizing account: <strong>{account.name}</strong>
        </div>

        <div className={styles.scopes}>
          <div className={styles.scopesTitle}>This application will be able to:</div>
          <ul>
            {consent.scopes.map((scope) => (
              <li key={scope}>{describeScope(scope)}</li>
            ))}
          </ul>
        </div>

        <div className={styles.buttons}>
          <Button
            size="large"
            loading={pending === 'allow'}
            disabled={pending !== null}
            onClick={onAllow}>
            Allow
          </Button>

          <Button
            size="large"
            variant="gray"
            loading={pending === 'deny'}
            disabled={pending !== null}
            onClick={onDeny}>
            Deny
          </Button>
        </div>
      </Card>
    </Cards>
  </div>
);

interface ClientLogoProps {
  name: string;
  logoUri: string | null;
}

// Self-asserted logo with a graceful fallback to an initial when it is missing
// or fails to load — MCP clients often omit a logo.
const ClientLogo = ({ name, logoUri }: ClientLogoProps) => {
  const [failed, setFailed] = useState(false);

  if (logoUri !== null && !failed) {
    return (
      <img
        className={styles.logo}
        src={logoUri}
        alt=""
        referrerPolicy="no-referrer"
        onError={() => setFailed(true)}
      />
    );
  }

  const initial = name.trim().charAt(0).toUpperCase();
  return (
    <div className={styles.logoFallback} aria-hidden>
      {initial === '' ? '?' : initial}
    </div>
  );
};
